import React, { useEffect, useState, useRef } from 'react';
import ArrowIcon from '../Icons/CircleArrow';
import { getDynamicURLClass } from './MasonryGallery';
import GalleryCarousel from './Carousel/GalleryCarousel.jsx';
import * as S from './Styles';

interface MediaItem {
  type: 'video' | 'image';
  url: string;
}

interface GalleryContentProps {
  mediaItems: MediaItem[];
  onGalleryScroll: (direction: 'left' | 'right') => void;
  selectedImageIndex: number;
  toggleShowModal: () => void;
  setSelectedIndex: (index: number) => void;
  videoIndex: number;
  videoThumbnail?: string;
  siteId?: string;
}

const GalleryContent: React.FC<GalleryContentProps> = ({
  mediaItems,
  onGalleryScroll,
  selectedImageIndex,
  toggleShowModal,
  setSelectedIndex,
  videoIndex,
  videoThumbnail,
}) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const galleryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const galleryElement = galleryRef.current;

    if (galleryElement) {
      const handleMouseEnter = () => setShowScrollButtons(true);
      const handleMouseLeave = () => setShowScrollButtons(false);

      galleryElement.addEventListener('mouseenter', handleMouseEnter);
      galleryElement.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        galleryElement.removeEventListener('mouseenter', handleMouseEnter);
        galleryElement.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, []);

  const getImageSrcSet = (baseImageUrl: string): string => {
    if (baseImageUrl.includes('www.fmicassets.com')) {
      return `
        https://www.fender.com/cdn-cgi/image/format=png,resize=height=auto,width=300/${baseImageUrl} 300w,
        https://www.fender.com/cdn-cgi/image/format=png,resize=height=auto,width=400/${baseImageUrl} 400w,
        https://www.fender.com/cdn-cgi/image/format=png,resize=height=auto,width=550/${baseImageUrl} 550w,
        https://www.fender.com/cdn-cgi/image/format=png,resize=height=auto,width=650/${baseImageUrl} 650w,
        https://www.fender.com/cdn-cgi/image/format=png,resize=height=auto,width=750/${baseImageUrl} 750w,
        https://www.fender.com/cdn-cgi/image/format=png,resize=height=auto,width=850/${baseImageUrl} 850w
      `;
    } else {
      return `
        ${baseImageUrl}?width=300 300w,
        ${baseImageUrl}?width=400 400w,
        ${baseImageUrl}?width=550 550w,
        ${baseImageUrl}?width=650 650w,
        ${baseImageUrl}?width=750 750w,
        ${baseImageUrl}?width=850 850w
      `;
    }
  };

  const getDefaultImageUrl = (baseImageUrl: string): string => {
    if (baseImageUrl.includes('www.fmicassets.com')) {
      return `https://www.fender.com/cdn-cgi/image/format=png,resize=height=auto,width=850/${baseImageUrl}`;
    } else {
      return `${baseImageUrl}?width=850`;
    }
  };

  const renderCarouselItem = (item: MediaItem, index: number) => {
    switch (item.type) {
      case 'video':
        return (
          <S.IframeWrapper key={item.url}>
            <S.StyledIframe
              key={selectedImageIndex === videoIndex ? item.url : 'video-placeholder'}
              allowFullScreen
              src={selectedImageIndex === videoIndex ? item.url : undefined}
              mobileDisplay={'none'}
              tabIndex={-1}
            />
            <S.IframeThumbnailWrapper>
              <S.IframeThumbnailDesktop />
              <S.IframeThumbnailMobile
                onClick={toggleShowModal}
                src={videoThumbnail}
                alt="Product Video Youtube Thumbnail"
              />
            </S.IframeThumbnailWrapper>
            <S.PlayIcon
              onClick={toggleShowModal}
              src={
                'https://www.fmicassets.com/sites/fender.com/themes/img/play-button.svg'
              }
              loading="lazy"
              className="iframe-play-icon"
              alt="play-icon"
              aria-hidden="true"
            />
          </S.IframeWrapper>
        );
      case 'image':
      default:
        const imageSrcSet = getImageSrcSet(item.url);
        const defaultImageUrl = getDefaultImageUrl(item.url);
        const urlClass = getDynamicURLClass(item.url);
        return (
          <S.HeroImage
            id={`image-${index}`}
            key={item.url}
            src={defaultImageUrl}
            srcSet={imageSrcSet}
            alt={`Gallery image ${index + 1} of ${mediaItems.length}`}
            urlClass={urlClass}
          />
        );
    }
  };

  const carouselItems = mediaItems.map((item, index) => renderCarouselItem(item, index));

  return (
    <S.GalleryMainContent isModal={false} ref={galleryRef}>
      <GalleryCarousel
        toggleShowModal={selectedImageIndex !== videoIndex ? toggleShowModal : () => {}}
        selectedIndex={selectedImageIndex}
        setSelectedIndex={setSelectedIndex}
        isZoomed={false}
      >
        {carouselItems}
      </GalleryCarousel>
      {mediaItems.length > 1 && showScrollButtons && (
        <>
          <S.LeftScrollButtonWrapper>
            <S.ArrowScrollButton className="gallery-button" tabIndex={-1} onClick={() => onGalleryScroll('left')}>
              <ArrowIcon direction={'left'} />
            </S.ArrowScrollButton>
          </S.LeftScrollButtonWrapper>
          <S.RightScrollButtonWrapper>
            <S.ArrowScrollButton className="gallery-button" tabIndex={-1} onClick={() => onGalleryScroll('right')}>
              <ArrowIcon direction={'right'} />
            </S.ArrowScrollButton>
          </S.RightScrollButtonWrapper>
        </>
      )}
    </S.GalleryMainContent>
  );
};

export default GalleryContent;
